import React from 'react';
import { CheckCircle, XCircle, QrCode, Clock, Settings } from 'lucide-react';

const FeatureUsage = ({ features }) => {
  if (!features) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Feature Adoption</h3>
        <p className="text-sm text-gray-500">No feature data available</p>
      </div>
    );
  }

  const featureList = [
    {
      name: 'QR Code Check-ins',
      description: 'Gyms using QR codes for check-ins',
      icon: QrCode,
      count: features.qrCodeUsage?.gymsUsing || 0,
      total: features.qrCodeUsage?.totalGyms || 0,
      percentage: features.qrCodeUsage?.percentage || 0,
      color: 'blue'
    },
    {
      name: 'Custom Reservation Durations',
      description: 'Gyms with custom duration settings',
      icon: Clock,
      count: features.customDurations?.gymsUsing || 0,
      total: features.customDurations?.totalGyms || 0,
      percentage: features.customDurations?.percentage || 0,
      color: 'green'
    },
    {
      name: 'Board-Level Settings',
      description: 'Boards with custom override settings',
      icon: Settings,
      count: features.boardSettings?.boardsUsing || 0,
      total: features.boardSettings?.totalBoards || 0,
      percentage: features.boardSettings?.percentage || 0,
      color: 'purple'
    },
    {
      name: 'Advanced Board Features',
      description: 'Boards with adjustable angles or LED systems',
      icon: CheckCircle,
      count: features.advancedFeatures?.boardsUsing || 0,
      total: features.advancedFeatures?.totalBoards || 0,
      percentage: features.advancedFeatures?.percentage || 0,
      color: 'orange'
    }
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      bar: 'bg-blue-500'
    },
    green: {
      bg: 'bg-green-100',
      text: 'text-green-600',
      bar: 'bg-green-500'
    },
    purple: {
      bg: 'bg-purple-100',
      text: 'text-purple-600',
      bar: 'bg-purple-500'
    },
    orange: {
      bg: 'bg-orange-100',
      text: 'text-orange-600',
      bar: 'bg-orange-500'
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Feature Adoption</h3>
        <p className="mt-1 text-sm text-gray-500">
          See which features gyms are actively using
        </p>
      </div>

      <div className="px-6 py-5 space-y-6">
        {featureList.map((feature) => {
          const Icon = feature.icon;
          const colors = colorClasses[feature.color];

          return (
            <div key={feature.name} className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className={`${colors.bg} rounded-lg p-2 mr-3`}>
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {feature.name}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {feature.description}
                    </p>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-lg font-semibold text-gray-900">
                    {feature.percentage.toFixed(0)}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {feature.count} of {feature.total}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`${colors.bar} h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${feature.percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall Insights */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Insights</h4>
        <ul className="space-y-1 text-sm text-gray-600">
          {featureList.some(f => f.percentage < 30) && (
            <li className="flex items-start">
              <XCircle className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                Some features have low adoption. Consider outreach or documentation improvements.
              </span>
            </li>
          )}
          {featureList.some(f => f.percentage > 70) && (
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                High adoption rates indicate strong feature value and usability.
              </span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default FeatureUsage;
